import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../api/client";
import BottomNav from "../components/BottomNav";
import { useFeatures } from "../context/FeaturesContext";
import type { CompanyEvent, Site, Department } from "../types";

/* ---------- data types --------------------------------------------------- */

interface LeaveEntry {
  employee_id: number;
  employee_name?: string;
  leave_type_id: number;
  leave_type_name: string;
  colour: string | null;
  start_date: string;
  end_date: string;
  status: string;
  total_days: number;
}

interface PublicHoliday {
  date: string;
  name: string;
  region_id: number | null;
  region_code: string | null;
  region_name: string | null;
}

interface Birthday {
  employee_id: number;
  employee_name: string;
  date: string;
}

interface Anniversary {
  employee_id: number;
  employee_name: string;
  date: string;
  years: number;
}

interface CalendarSite {
  id: number;
  name: string;
  colour: string | null;
  region_id: number;
}

interface CalendarData {
  entries: LeaveEntry[];
  holidays: PublicHoliday[];
  birthdays: Birthday[];
  anniversaries: Anniversary[];
  sites: CalendarSite[];
  colour_settings: Record<string, string>;
}

type ItemKind = "leave" | "holiday" | "birthday" | "anniversary" | "event";

interface CalendarItem {
  kind: ItemKind;
  title: string;
  subtitle: string;
  colour: string;
  textColour: string;
  startDate: string;
  endDate: string;
  isPending: boolean;
  raw: LeaveEntry | PublicHoliday | Birthday | Anniversary | CompanyEvent;
}

/* ---------- constants ---------------------------------------------------- */

const VIEWS = ["month", "week", "list"] as const;
type View = (typeof VIEWS)[number];

const DAY_LABELS_KEY = [
  "companyCalendar.mon",
  "companyCalendar.tue",
  "companyCalendar.wed",
  "companyCalendar.thu",
  "companyCalendar.fri",
  "companyCalendar.sat",
  "companyCalendar.sun",
];

const DEFAULT_EVENT_COLOURS: Record<string, string> = {
  holiday: "#E5E7EB",
  birthday: "#FCD34D",
  anniversary: "#14B8A6",
  event: "#6366F1",
};

const DATE_FMT = new Intl.DateTimeFormat(undefined, { dateStyle: "medium" });

/* ---------- colour helpers ----------------------------------------------- */

function luminance(hex: string): number {
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16) / 255;
  const g = parseInt(h.substring(2, 4), 16) / 255;
  const b = parseInt(h.substring(4, 6), 16) / 255;
  const toLinear = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

function autoTextColour(bg: string): string {
  return luminance(bg) < 0.5 ? "#FFFFFF" : "#1F2937";
}

/* ---------- date helpers ------------------------------------------------- */

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}
function toISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function toMonthISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function parseDate(s: string): Date {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}
function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function getMonday(d: Date): Date {
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  const mon = new Date(d);
  mon.setDate(mon.getDate() + diff);
  return mon;
}
function rangeOverlapsDay(startStr: string, endStr: string, day: Date): boolean {
  const s = parseDate(startStr);
  const e = parseDate(endStr);
  s.setHours(0, 0, 0, 0);
  e.setHours(23, 59, 59, 999);
  const dayStart = new Date(day);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(day);
  dayEnd.setHours(23, 59, 59, 999);
  return s <= dayEnd && e >= dayStart;
}

/* ---------- holiday region helpers --------------------------------------- */

function deduplicateHolidays(holidays: PublicHoliday[]): PublicHoliday[] {
  const grouped = new Map<string, PublicHoliday[]>();
  for (const h of holidays) {
    const key = `${h.date}|${h.name}`;
    const existing = grouped.get(key);
    if (existing) existing.push(h);
    else grouped.set(key, [h]);
  }
  const result: PublicHoliday[] = [];
  for (const [, group] of grouped) {
    if (group.length === 1) {
      result.push(group[0]);
    } else {
      const codes = group.map((h) => h.region_code).filter(Boolean);
      const names = group.map((h) => h.region_name).filter(Boolean);
      result.push({
        ...group[0],
        region_code: codes.join(", "),
        region_name: names.join(", "),
      });
    }
  }
  return result;
}

/* ---------- build items -------------------------------------------------- */

function resolveTextColour(
  colourSettings: Record<string, string>,
  settingsKey: string,
  bgColour: string,
): string {
  const configured = colourSettings[settingsKey];
  if (configured) return configured;
  return autoTextColour(bgColour);
}

function buildCalendarItems(
  events: CompanyEvent[],
  data: CalendarData,
  colourSettings: Record<string, string>,
  birthdaysEnabled: boolean,
  anniversariesEnabled: boolean,
): CalendarItem[] {
  const items: CalendarItem[] = [];
  const holidayColour = colourSettings.calendar_colour_holiday || DEFAULT_EVENT_COLOURS.holiday;
  const birthdayColour = colourSettings.calendar_colour_birthday || DEFAULT_EVENT_COLOURS.birthday;
  const anniversaryColour = colourSettings.calendar_colour_anniversary || DEFAULT_EVENT_COLOURS.anniversary;
  const eventColour = colourSettings.calendar_colour_event || DEFAULT_EVENT_COLOURS.event;

  for (const ev of events) {
    const bg = ev.colour || eventColour;
    items.push({
      kind: "event",
      title: ev.title,
      subtitle: ev.description || "",
      colour: bg,
      textColour: resolveTextColour(colourSettings, "calendar_text_colour_event", bg),
      startDate: ev.start_date,
      endDate: ev.end_date,
      isPending: false,
      raw: ev,
    });
  }

  for (const le of data.entries) {
    const isPending = le.status === "pending";
    const bg = le.colour || "#0872c1";
    items.push({
      kind: "leave",
      title: le.employee_name ?? "Employee",
      subtitle: le.leave_type_name,
      colour: bg,
      textColour: autoTextColour(bg),
      startDate: le.start_date,
      endDate: le.end_date,
      isPending,
      raw: le,
    });
  }

  const dedupedHolidays = deduplicateHolidays(data.holidays);
  for (const h of dedupedHolidays) {
    const regionTag = h.region_code ? ` (${h.region_code.toUpperCase()})` : "";
    items.push({
      kind: "holiday",
      title: `${h.name}${regionTag}`,
      subtitle: h.region_name || "",
      colour: holidayColour,
      textColour: resolveTextColour(colourSettings, "calendar_text_colour_holiday", holidayColour),
      startDate: h.date,
      endDate: h.date,
      isPending: false,
      raw: h,
    });
  }

  if (birthdaysEnabled) {
    for (const b of data.birthdays) {
      items.push({
        kind: "birthday",
        title: b.employee_name,
        subtitle: "",
        colour: birthdayColour,
        textColour: resolveTextColour(colourSettings, "calendar_text_colour_birthday", birthdayColour),
        startDate: b.date,
        endDate: b.date,
        isPending: false,
        raw: b,
      });
    }
  }

  if (anniversariesEnabled) {
    for (const a of data.anniversaries) {
      items.push({
        kind: "anniversary",
        title: a.employee_name,
        subtitle: `${a.years}`,
        colour: anniversaryColour,
        textColour: resolveTextColour(colourSettings, "calendar_text_colour_anniversary", anniversaryColour),
        startDate: a.date,
        endDate: a.date,
        isPending: false,
        raw: a,
      });
    }
  }

  items.sort((a, b) => a.startDate.localeCompare(b.startDate));
  return items;
}

function itemsForDay(items: CalendarItem[], day: Date): CalendarItem[] {
  return items.filter((it) => rangeOverlapsDay(it.startDate, it.endDate, day));
}

/* ---------- filter defaults ---------------------------------------------- */

const FILTER_DEFAULTS = {
  sites: "",
  department: "",
  leaveType: "",
  search: "",
  showLeave: "1",
  showEvents: "1",
  showHolidays: "1",
  showBirthdays: "1",
  showAnniversaries: "1",
};

/* ---------- main component ----------------------------------------------- */

export default function CompanyCalendarPage() {
  const { t } = useTranslation();
  const { isEnabled } = useFeatures();
  const [searchParams, setSearchParams] = useSearchParams();

  const [current, setCurrent] = useState(() => startOfMonth(new Date()));
  const [view, setView] = useState<View>(
    () => (searchParams.get("view") as View) || "month",
  );
  const [events, setEvents] = useState<CompanyEvent[]>([]);
  const [calData, setCalData] = useState<CalendarData>({
    entries: [],
    holidays: [],
    birthdays: [],
    anniversaries: [],
    sites: [],
    colour_settings: {},
  });
  const [loading, setLoading] = useState(false);
  const [popupDay, setPopupDay] = useState<Date | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [allSites, setAllSites] = useState<Site[]>([]);
  const [allDepartments, setAllDepartments] = useState<Department[]>([]);
  const [allLeaveTypes, setAllLeaveTypes] = useState<{ id: number; name: string }[]>([]);

  // Filters from URL
  const filterSites = searchParams.get("sites") || "";
  const filterDept = searchParams.get("department") || "";
  const filterLeaveType = searchParams.get("leaveType") || "";
  const filterSearch = (searchParams.get("search") || "").toLowerCase();
  const showLeave = searchParams.get("showLeave") !== "0";
  const showEvents = searchParams.get("showEvents") !== "0";
  const showHolidays = searchParams.get("showHolidays") !== "0";
  const showBirthdays = searchParams.get("showBirthdays") !== "0";
  const showAnniversaries = searchParams.get("showAnniversaries") !== "0";

  const birthdaysEnabled = isEnabled("birthday_celebrations_enabled");
  const anniversariesEnabled = isEnabled("anniversary_celebrations_enabled");

  const setFilter = useCallback(
    (key: string, value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (!value || value === (FILTER_DEFAULTS as Record<string, string>)[key]) {
            next.delete(key);
          } else {
            next.set(key, value);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  useEffect(() => {
    void Promise.all([
      api.get<Site[]>("/api/sites").catch(() => []),
      api.get<Department[]>("/api/departments").catch(() => []),
      api.get<{ id: number; name: string }[]>("/api/leave/types").catch(() => []),
    ]).then(([s, d, lt]) => {
      setAllSites(s);
      setAllDepartments(d);
      setAllLeaveTypes(lt);
    });
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const monthStart = startOfMonth(current);
    const monthEnd = endOfMonth(current);

    const [evResult, leaveResult] = await Promise.all([
      api
        .get<CompanyEvent[]>(
          `/api/company-events?start=${toISO(monthStart)}&end=${toISO(monthEnd)}`,
        )
        .catch(() => [] as CompanyEvent[]),
      api
        .get<CalendarData>(`/api/leave/calendar?month=${toMonthISO(current)}`)
        .catch(
          () =>
            ({
              entries: [],
              holidays: [],
              birthdays: [],
              anniversaries: [],
              sites: [],
              colour_settings: {},
            }) as CalendarData,
        ),
    ]);
    setEvents(evResult);
    setCalData(leaveResult);
    setLoading(false);
  }, [current]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const colourSettings = calData.colour_settings || {};

  const allItems = useMemo(
    () =>
      buildCalendarItems(events, calData, colourSettings, birthdaysEnabled, anniversariesEnabled),
    [events, calData, colourSettings, birthdaysEnabled, anniversariesEnabled],
  );

  // Apply filters
  const filteredItems = useMemo(() => {
    const selectedSiteIds = filterSites
      ? new Set(filterSites.split(",").map(Number))
      : null;

    return allItems.filter((item) => {
      if (!showLeave && item.kind === "leave") return false;
      if (!showEvents && item.kind === "event") return false;
      if (!showHolidays && item.kind === "holiday") return false;
      if (!showBirthdays && item.kind === "birthday") return false;
      if (!showAnniversaries && item.kind === "anniversary") return false;

      if (filterSearch && !item.title.toLowerCase().includes(filterSearch) && !item.subtitle.toLowerCase().includes(filterSearch)) {
        return false;
      }

      if (selectedSiteIds && item.kind === "event") {
        const ev = item.raw as CompanyEvent;
        if (ev.site_id && !selectedSiteIds.has(ev.site_id)) return false;
      }

      if (filterLeaveType && item.kind === "leave") {
        const le = item.raw as LeaveEntry;
        if (String(le.leave_type_id) !== filterLeaveType) return false;
      }

      return true;
    });
  }, [
    allItems,
    filterSites,
    filterSearch,
    filterLeaveType,
    showLeave,
    showEvents,
    showHolidays,
    showBirthdays,
    showAnniversaries,
  ]);

  const handleViewChange = (v: View) => {
    setView(v);
    setFilter("view", v === "month" ? "" : v);
    setPopupDay(null);
  };

  const prevMonth = () => {
    setCurrent((c) => addMonths(c, -1));
    setPopupDay(null);
  };
  const nextMonth = () => {
    setCurrent((c) => addMonths(c, 1));
    setPopupDay(null);
  };

  const monthLabel = current.toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });

  const legendItems = useMemo(() => {
    const items: { label: string; colour: string; dashed?: boolean }[] = [];
    if (showLeave) {
      const usedTypes = new Set(
        calData.entries.map((e) => e.leave_type_name),
      );
      for (const name of usedTypes) {
        const entry = calData.entries.find((e) => e.leave_type_name === name);
        items.push({
          label: name,
          colour: entry?.colour || "#0872c1",
        });
      }
      items.push({
        label: t("companyCalendar.pending"),
        colour: "#F59E0B",
        dashed: true,
      });
    }
    if (showHolidays) {
      items.push({
        label: t("companyCalendar.publicHoliday"),
        colour: colourSettings.calendar_colour_holiday || DEFAULT_EVENT_COLOURS.holiday,
      });
    }
    if (showEvents) {
      items.push({
        label: t("companyCalendar.companyEvent"),
        colour: colourSettings.calendar_colour_event || DEFAULT_EVENT_COLOURS.event,
      });
    }
    if (showBirthdays && birthdaysEnabled) {
      items.push({
        label: t("companyCalendar.birthday"),
        colour: colourSettings.calendar_colour_birthday || DEFAULT_EVENT_COLOURS.birthday,
      });
    }
    if (showAnniversaries && anniversariesEnabled) {
      items.push({
        label: t("companyCalendar.anniversary"),
        colour: colourSettings.calendar_colour_anniversary || DEFAULT_EVENT_COLOURS.anniversary,
      });
    }
    return items;
  }, [
    showLeave,
    showHolidays,
    showEvents,
    showBirthdays,
    showAnniversaries,
    birthdaysEnabled,
    anniversariesEnabled,
    calData.entries,
    colourSettings,
    t,
  ]);

  const hasActiveFilters =
    filterSites !== "" ||
    filterDept !== "" ||
    filterLeaveType !== "" ||
    filterSearch !== "" ||
    !showLeave ||
    !showEvents ||
    !showHolidays ||
    !showBirthdays ||
    !showAnniversaries;

  return (
    <div className="flex min-h-dvh flex-col safe-x">
      <header className="safe-top px-5 pt-4 pb-2">
        <h1 className="text-2xl font-semibold text-gray-900">
          {t("companyCalendar.title")}
        </h1>
      </header>

      {/* Month nav + view toggle */}
      <div className="flex items-center justify-between px-5 py-2">
        <button type="button" onClick={prevMonth} className="rounded-lg px-3 py-1 text-sm font-medium text-gray-600 hover:bg-gray-100">
          ‹
        </button>
        <span className="text-base font-semibold text-gray-900">{monthLabel}</span>
        <button type="button" onClick={nextMonth} className="rounded-lg px-3 py-1 text-sm font-medium text-gray-600 hover:bg-gray-100">
          ›
        </button>
      </div>

      <div className="flex items-center justify-between border-b border-gray-200 px-2">
        <nav className="flex text-sm">
          {VIEWS.map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => handleViewChange(v)}
              className={[
                "whitespace-nowrap px-4 py-2 text-sm font-semibold capitalize",
                view === v
                  ? "border-b-2 border-pme-blue text-pme-blue"
                  : "text-gray-500 hover:text-gray-800",
              ].join(" ")}
            >
              {t(`companyCalendar.view.${v}`)}
            </button>
          ))}
        </nav>
        <button
          type="button"
          onClick={() => setFiltersOpen((o) => !o)}
          className={[
            "flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-medium",
            hasActiveFilters
              ? "bg-pme-blue text-white"
              : "text-gray-600 hover:bg-gray-100",
          ].join(" ")}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
          {t("companyCalendar.filters")}
          {hasActiveFilters && (
            <span className="ml-1 flex h-4 w-4 items-center justify-center rounded-full bg-white text-[10px] font-bold text-pme-blue">
              !
            </span>
          )}
        </button>
      </div>

      {/* Filter panel */}
      {filtersOpen && (
        <FilterPanel
          allSites={allSites}
          allDepartments={allDepartments}
          allLeaveTypes={allLeaveTypes}
          filterSites={filterSites}
          filterDept={filterDept}
          filterLeaveType={filterLeaveType}
          filterSearch={searchParams.get("search") || ""}
          showLeave={showLeave}
          showEvents={showEvents}
          showHolidays={showHolidays}
          showBirthdays={showBirthdays}
          showAnniversaries={showAnniversaries}
          birthdaysEnabled={birthdaysEnabled}
          anniversariesEnabled={anniversariesEnabled}
          setFilter={setFilter}
          onClear={() => {
            setSearchParams((prev) => {
              const next = new URLSearchParams();
              const v = prev.get("view");
              if (v) next.set("view", v);
              return next;
            }, { replace: true });
          }}
        />
      )}

      {/* Legend — always visible, inline below filters */}
      {legendItems.length > 0 && (
        <div className="border-b border-gray-200 bg-white px-5 py-2">
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {legendItems.map((li) => (
              <div key={li.label} className="flex items-center gap-1.5">
                <span
                  className="inline-block h-2.5 w-2.5 rounded-full"
                  style={{
                    backgroundColor: li.dashed ? "transparent" : li.colour,
                    border: li.dashed ? "1.5px dashed #D97706" : undefined,
                  }}
                />
                <span className="text-xs text-gray-600">{li.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="flex-1 overflow-y-auto px-5 py-4 pb-24">
        {loading && (
          <p className="py-8 text-center text-sm text-gray-400">
            {t("companyCalendar.loading")}
          </p>
        )}

        {!loading && view === "month" && (
          <MonthView
            current={current}
            items={filteredItems}
            popupDay={popupDay}
            onSelectDay={setPopupDay}
            t={t}
          />
        )}

        {!loading && view === "week" && (
          <WeekView current={current} items={filteredItems} t={t} />
        )}

        {!loading && view === "list" && <ListView items={filteredItems} t={t} />}

        {/* Popup for day details */}
        {popupDay && (
          <DayPopup
            day={popupDay}
            items={itemsForDay(filteredItems, popupDay)}
            onClose={() => setPopupDay(null)}
            t={t}
          />
        )}
      </main>

      <BottomNav />
    </div>
  );
}

/* ---------- Filter Panel ------------------------------------------------- */

function FilterPanel({
  allSites,
  allDepartments,
  allLeaveTypes,
  filterSites,
  filterDept,
  filterLeaveType,
  filterSearch,
  showLeave,
  showEvents,
  showHolidays,
  showBirthdays,
  showAnniversaries,
  birthdaysEnabled,
  anniversariesEnabled,
  setFilter,
  onClear,
}: {
  allSites: Site[];
  allDepartments: Department[];
  allLeaveTypes: { id: number; name: string }[];
  filterSites: string;
  filterDept: string;
  filterLeaveType: string;
  filterSearch: string;
  showLeave: boolean;
  showEvents: boolean;
  showHolidays: boolean;
  showBirthdays: boolean;
  showAnniversaries: boolean;
  birthdaysEnabled: boolean;
  anniversariesEnabled: boolean;
  setFilter: (key: string, value: string) => void;
  onClear: () => void;
}) {
  const { t } = useTranslation();
  const selectedSiteIds = new Set(
    filterSites ? filterSites.split(",").map(Number) : [],
  );

  const toggleSite = (id: number) => {
    const next = new Set(selectedSiteIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setFilter("sites", [...next].join(","));
  };

  return (
    <div className="border-b border-gray-200 bg-gray-50 px-5 py-3">
      {/* Search */}
      <input
        type="text"
        value={filterSearch}
        onChange={(e) => setFilter("search", e.target.value)}
        placeholder={t("companyCalendar.searchPlaceholder")}
        className="mb-3 w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-pme-blue focus:outline-none focus:ring-1 focus:ring-pme-blue"
      />

      {/* Type toggles */}
      <div className="mb-3 flex flex-wrap gap-2">
        <ToggleChip label={t("companyCalendar.leave")} active={showLeave} onChange={(v) => setFilter("showLeave", v ? "1" : "0")} />
        <ToggleChip label={t("companyCalendar.events")} active={showEvents} onChange={(v) => setFilter("showEvents", v ? "1" : "0")} />
        <ToggleChip label={t("companyCalendar.holidays")} active={showHolidays} onChange={(v) => setFilter("showHolidays", v ? "1" : "0")} />
        {birthdaysEnabled && (
          <ToggleChip label={t("companyCalendar.birthdays")} active={showBirthdays} onChange={(v) => setFilter("showBirthdays", v ? "1" : "0")} />
        )}
        {anniversariesEnabled && (
          <ToggleChip label={t("companyCalendar.anniversaries")} active={showAnniversaries} onChange={(v) => setFilter("showAnniversaries", v ? "1" : "0")} />
        )}
      </div>

      {/* Site multi-select chips */}
      {allSites.length > 0 && (
        <div className="mb-3">
          <label className="mb-1 block text-xs font-medium text-gray-500">
            {t("companyCalendar.filterBySite")}
          </label>
          <div className="flex flex-wrap gap-1.5">
            {allSites.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => toggleSite(s.id)}
                className={[
                  "rounded-full px-2.5 py-0.5 text-xs font-medium transition",
                  selectedSiteIds.has(s.id)
                    ? "bg-pme-blue text-white"
                    : "bg-white text-gray-600 ring-1 ring-gray-300 hover:bg-gray-100",
                ].join(" ")}
              >
                {s.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Department & leave type selects */}
      <div className="flex flex-wrap gap-3">
        {allDepartments.length > 0 && (
          <select
            value={filterDept}
            onChange={(e) => setFilter("department", e.target.value)}
            className="rounded-lg border border-gray-300 px-2 py-1 text-xs"
          >
            <option value="">{t("companyCalendar.allDepartments")}</option>
            {allDepartments.map((d) => (
              <option key={d.id} value={String(d.id)}>
                {d.name}{d.site_name ? ` (${d.site_name})` : ""}
              </option>
            ))}
          </select>
        )}

        {allLeaveTypes.length > 0 && (
          <select
            value={filterLeaveType}
            onChange={(e) => setFilter("leaveType", e.target.value)}
            className="rounded-lg border border-gray-300 px-2 py-1 text-xs"
          >
            <option value="">{t("companyCalendar.allLeaveTypes")}</option>
            {allLeaveTypes.map((lt) => (
              <option key={lt.id} value={String(lt.id)}>
                {lt.name}
              </option>
            ))}
          </select>
        )}

        <button
          type="button"
          onClick={onClear}
          className="rounded-lg px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
        >
          {t("companyCalendar.clearFilters")}
        </button>
      </div>
    </div>
  );
}

function ToggleChip({
  label,
  active,
  onChange,
}: {
  label: string;
  active: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!active)}
      className={[
        "rounded-full px-2.5 py-0.5 text-xs font-medium transition",
        active
          ? "bg-pme-blue text-white"
          : "bg-gray-200 text-gray-500 line-through",
      ].join(" ")}
    >
      {label}
    </button>
  );
}

/* ---------- pill helpers ------------------------------------------------- */

function itemLabel(item: CalendarItem, t: (k: string) => string): string {
  switch (item.kind) {
    case "leave": {
      const pending = item.isPending ? ` (${t("companyCalendar.pending")})` : "";
      return `${item.title} - ${item.subtitle}${pending}`;
    }
    case "birthday":
      return `${item.title}`;
    case "anniversary":
      return `${item.title} (${item.subtitle} ${t("companyCalendar.years")})`;
    case "holiday":
      return `${item.title}`;
    case "event":
      return `${item.title}`;
    default:
      return item.title;
  }
}

/* ---------- Month View --------------------------------------------------- */

function MonthView({
  current,
  items,
  popupDay,
  onSelectDay,
  t,
}: {
  current: Date;
  items: CalendarItem[];
  popupDay: Date | null;
  onSelectDay: (d: Date) => void;
  t: (k: string) => string;
}) {
  const year = current.getFullYear();
  const month = current.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = endOfMonth(current);

  const startDow = firstDay.getDay();
  const offset = startDow === 0 ? 6 : startDow - 1;

  const cells: (Date | null)[] = [];
  for (let i = 0; i < offset; i++) cells.push(null);
  for (let d = 1; d <= lastDay.getDate(); d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);

  const today = new Date();

  return (
    <div>
      <div className="mb-1 grid grid-cols-7 gap-px text-center text-xs font-medium text-gray-500">
        {DAY_LABELS_KEY.map((k) => (
          <div key={k} className="py-1">
            {t(k)}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-px rounded-lg border border-gray-200 bg-gray-200">
        {cells.map((cell, idx) => {
          if (!cell) {
            return <div key={`empty-${idx}`} className="min-h-[4.5rem] bg-gray-50" />;
          }
          const dayItems = itemsForDay(items, cell);
          const isToday = isSameDay(cell, today);
          const isSelected = popupDay ? isSameDay(cell, popupDay) : false;
          const isWeekend = cell.getDay() === 0 || cell.getDay() === 6;

          return (
            <button
              key={toISO(cell)}
              type="button"
              onClick={() => onSelectDay(cell)}
              className={[
                "flex min-h-[4.5rem] flex-col p-1 text-left text-sm transition",
                isSelected ? "bg-blue-50 ring-2 ring-inset ring-pme-blue" : isWeekend ? "bg-gray-50 hover:bg-gray-100" : "bg-white hover:bg-gray-50",
              ].join(" ")}
            >
              <span
                className={[
                  "mb-0.5 flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-medium",
                  isToday ? "bg-pme-blue text-white" : "text-gray-700",
                ].join(" ")}
              >
                {cell.getDate()}
              </span>
              <div className="flex flex-col gap-px overflow-hidden">
                {dayItems.slice(0, 3).map((it, i) => (
                  <div
                    key={i}
                    className={[
                      "truncate rounded px-1 font-medium leading-tight",
                      it.isPending ? "opacity-60" : "",
                    ].join(" ")}
                    style={{
                      backgroundColor: it.colour,
                      color: it.textColour,
                      borderLeft: it.isPending ? "2px dashed #D97706" : undefined,
                      fontSize: "11px",
                    }}
                    title={itemLabel(it, t)}
                  >
                    {it.kind === "leave"
                      ? it.title.split(" ")[0]
                      : it.kind === "birthday"
                        ? `${it.title.split(" ")[0]}`
                        : it.kind === "anniversary"
                          ? `${it.title.split(" ")[0]}`
                          : it.kind === "holiday"
                            ? `${it.title}`
                            : `${it.title}`}
                  </div>
                ))}
                {dayItems.length > 3 && (
                  <span className="text-[10px] font-medium text-gray-400">
                    +{dayItems.length - 3} {t("companyCalendar.more")}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ---------- Week View ---------------------------------------------------- */

function WeekView({
  current,
  items,
  t,
}: {
  current: Date;
  items: CalendarItem[];
  t: (k: string) => string;
}) {
  const today = new Date();
  const monday = getMonday(
    new Date(current.getFullYear(), current.getMonth(), 15),
  );
  const days: Date[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    days.push(d);
  }

  return (
    <div className="space-y-2">
      {days.map((day) => {
        const dayItems = itemsForDay(items, day);
        const isToday = isSameDay(day, today);
        return (
          <div
            key={toISO(day)}
            className={[
              "rounded-xl border p-3",
              isToday
                ? "border-pme-blue bg-pme-blue/5"
                : "border-gray-200 bg-white",
            ].join(" ")}
          >
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-900">
                {day.toLocaleDateString(undefined, {
                  weekday: "short",
                  day: "numeric",
                  month: "short",
                })}
              </span>
              {dayItems.length > 0 && (
                <span className="text-xs text-gray-500">
                  {dayItems.length}
                </span>
              )}
            </div>
            {dayItems.length > 0 && (
              <div className="mt-2 space-y-1">
                {dayItems.map((it, i) => (
                  <div
                    key={i}
                    className={[
                      "flex items-center gap-2 rounded px-2 py-1",
                      it.isPending ? "opacity-60" : "",
                    ].join(" ")}
                    style={{ borderLeft: `3px solid ${it.colour}` }}
                  >
                    <span className="truncate text-xs font-medium text-gray-700" style={{ minWidth: 0 }}>
                      {itemLabel(it, t)}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {dayItems.length === 0 && (
              <p className="mt-1 text-xs text-gray-400">
                {t("companyCalendar.noEvents")}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ---------- List View ---------------------------------------------------- */

function ListView({
  items,
  t,
}: {
  items: CalendarItem[];
  t: (k: string) => string;
}) {
  if (items.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-gray-400">
        {t("companyCalendar.noEventsThisMonth")}
      </p>
    );
  }

  const grouped: Record<string, CalendarItem[]> = {};
  for (const item of items) {
    const key = item.startDate;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(item);
  }
  const sortedDates = Object.keys(grouped).sort();

  return (
    <div className="space-y-4">
      {sortedDates.map((dateStr) => (
        <div key={dateStr}>
          <h3 className="mb-1.5 text-xs font-semibold uppercase text-gray-500">
            {DATE_FMT.format(parseDate(dateStr))}
          </h3>
          <div className="space-y-1">
            {grouped[dateStr].map((it, i) => (
              <div
                key={i}
                className={[
                  "flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-3",
                  it.isPending ? "opacity-60 border-dashed" : "",
                ].join(" ")}
              >
                <span
                  className="mt-0.5 inline-block h-3 w-3 flex-shrink-0 rounded-full"
                  style={{ backgroundColor: it.colour }}
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {itemLabel(it, t)}
                  </p>
                  {it.startDate !== it.endDate && (
                    <p className="mt-0.5 text-xs text-gray-500">
                      {DATE_FMT.format(parseDate(it.startDate))} –{" "}
                      {DATE_FMT.format(parseDate(it.endDate))}
                    </p>
                  )}
                  {it.kind === "holiday" && it.subtitle && (
                    <p className="mt-0.5 text-xs text-gray-500">
                      {t("companyCalendar.appliesTo")}: {it.subtitle}
                    </p>
                  )}
                  {it.kind === "leave" && it.isPending && (
                    <span className="mt-1 inline-block rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700">
                      {t("companyCalendar.pending")}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------- Day Popup ---------------------------------------------------- */

function popupItemTitle(it: CalendarItem, t: (k: string) => string): string {
  switch (it.kind) {
    case "leave":
      return `${it.title} — ${it.subtitle}`;
    case "birthday":
      return `${it.title}'s ${t("companyCalendar.birthday")}`;
    case "anniversary":
      return `${it.title} — ${it.subtitle} ${t("companyCalendar.yearsAtCompany")}`;
    case "holiday":
      return `${it.title}`;
    case "event":
      return `${it.title}`;
    default:
      return it.title;
  }
}

function DayPopup({
  day,
  items,
  onClose,
  t,
}: {
  day: Date;
  items: CalendarItem[];
  onClose: () => void;
  t: (k: string) => string;
}) {
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div
        ref={popupRef}
        className="flex max-h-[70vh] w-full flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        style={{ minWidth: "min(400px, 90vw)", maxWidth: "600px", minHeight: "300px" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h3 className="text-lg font-bold text-gray-900">
            {day.toLocaleDateString(undefined, {
              weekday: "long",
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 && (
            <p className="py-8 text-center text-sm text-gray-400">{t("companyCalendar.noEventsOnDay")}</p>
          )}

          {items.length > 0 && (
            <div className="space-y-3">
              {items.map((it, i) => (
                <div
                  key={i}
                  className={[
                    "rounded-lg border bg-white p-4",
                    it.isPending ? "border-dashed border-amber-300 bg-amber-50/50" : "border-gray-200",
                  ].join(" ")}
                  style={{ borderLeftWidth: "4px", borderLeftColor: it.colour }}
                >
                  <div className="flex items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-900">
                        {popupItemTitle(it, t)}
                      </p>

                      {it.kind === "holiday" && it.subtitle && (
                        <p className="mt-1 text-xs text-gray-500">
                          {t("companyCalendar.appliesTo")}: {it.subtitle}
                        </p>
                      )}

                      {it.kind === "leave" && (
                        <p className="mt-1 text-xs text-gray-500">
                          {(it.raw as LeaveEntry).total_days}{" "}
                          {(it.raw as LeaveEntry).total_days === 1
                            ? t("companyCalendar.day")
                            : t("companyCalendar.days")}
                          {it.startDate !== it.endDate && (
                            <span>
                              {" · "}
                              {DATE_FMT.format(parseDate(it.startDate))} – {DATE_FMT.format(parseDate(it.endDate))}
                            </span>
                          )}
                        </p>
                      )}

                      {it.kind === "event" && (it.raw as CompanyEvent).description && (
                        <p className="mt-1 text-xs text-gray-500">
                          {(it.raw as CompanyEvent).description}
                        </p>
                      )}

                      {it.isPending && (
                        <span className="mt-1.5 inline-block rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700">
                          {t("companyCalendar.pending")}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
